import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { debounce } from "lodash";

import { generate_uid } from "@/utils";

type PropTypes = {
  errors: string[];
  hide_errors: boolean;
  max_errors_to_show?: number;
};
type EmitTypes = {
  (e: "has_rendered_errors", value: boolean): void;
};
type VisibleError = {
  error_msg: string;
  uid: number;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'InputErrors',
  props: {
    errors: null,
    hide_errors: { type: Boolean },
    max_errors_to_show: { default: 1 }
  },
  emits: ["has_rendered_errors"],
  setup(__props: any, { emit }: { emit: ({
  (e: "has_rendered_errors", value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { errors: string[], hide_errors: boolean, max_errors_to_show: number };



// Allows parent component to know if any errors are rendered for consistent styling,
// i.e. a parent component might put a red border around the input when errors are
// displayed


const debounced_errors = ref<VisibleError[]>([]);
const errors_to_render = computed(() => {
  if (props.max_errors_to_show === null) {
    return debounced_errors.value;
  } else {
    return debounced_errors.value.slice(0, props.max_errors_to_show);
  }
});

const debounce_cancel_map = new Map<string, () => void>();

watch(
  () => props.errors,
  (new_errors: string[], old_errors: string[] | undefined) => {
    const removed_errors = old_errors?.filter(
      (err) => !new_errors.includes(err),
    );
    const added_errors = new_errors.filter((err) => !old_errors?.includes(err));

    // immediately remove resolved errors and cancel any debounced additions
    // to visible errors
    debounced_errors.value = debounced_errors.value.filter((err) =>
      new_errors.includes(err.error_msg),
    );
    removed_errors?.forEach((err) => {
      const cancel = debounce_cancel_map.get(err);
      if (cancel) {
        cancel();
      }
      debounce_cancel_map.delete(err);
    });

    // Debounce additions to visible errors for smoother UX, keeping track of
    // the cancel functions
    added_errors.forEach((err) => {
      const debounce_add = debounce(() => {
        debounced_errors.value.push({ error_msg: err, uid: generate_uid() });
        debounce_cancel_map.delete(err);
        emit("has_rendered_errors", true);
      }, 500);
      debounce_cancel_map.set(err, () => debounce_add.cancel());
      debounce_add();
    });

    if (debounced_errors.value.length === 0) {
      emit("has_rendered_errors", false);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  debounce_cancel_map.forEach((cancel) => cancel());
});

return { __sfc: true,props, emit, debounced_errors, errors_to_render, debounce_cancel_map }
}

})